// RE₿EL MARKET SIMULATOR™ — Historical scenarios
// 4 hardcoded scenarios with narrative arcs (80 candles each).
// Each scenario defines: volatility, trend, event injections, base metrics.

const baseCandle = (startPrice) => ({
  open: startPrice,
  high: startPrice,
  low: startPrice,
  close: startPrice,
  volume: 500,
});

export const SCENARIOS = {
  ftx_collapse: {
    id: "ftx_collapse",
    name: { es: "FTX Collapse — Noviembre 2022", en: "FTX Collapse — November 2022", sk: "FTX kolaps — november 2022", de: "FTX Collapse — November 2022", "zh-CN": "FTX Collapse — 2022年11月" },
    free: true,
    startPrice: 21000,
    totalCandles: 80,
    initialCapital: 10000,
    base: { funding: -0.01, openInterest: 1, fearGreed: 28, liquidations: 120, dominance: 41 },
    // dynamic params per candle index
    params: (i) => {
      // normal then sudden crash at vela 30 + fake rebound at 55
      if (i < 28) return { vol: 0.012, trend: -0.1 };
      if (i < 36) return { vol: 0.06, trend: -0.9 }; // FTX crash
      if (i < 52) return { vol: 0.025, trend: -0.3 };
      if (i < 58) return { vol: 0.018, trend: 0.4 }; // fake rebound
      if (i < 72) return { vol: 0.04, trend: -0.7 }; // resumed dump
      return { vol: 0.02, trend: -0.2 };
    },
    events: {
      30: {
        type: "alert_red",
        text: { es: "BREAKING: FTX detiene retiros", en: "BREAKING: FTX halts withdrawals", sk: "BREAKING: FTX zastavuje výbery", de: "BREAKING: FTX stoppt Auszahlungen", "zh-CN": "突发：FTX停止提款" },
      },
      55: {
        type: "alert_amber",
        text: { es: "Rebote sospechoso · Volumen débil", en: "Suspicious bounce · Weak volume", sk: "Podozrivý odraz · Slabý objem", de: "Verdächtiger Rebound · Schwaches Volumen", "zh-CN": "可疑反弹 · 交易量疲软" },
      },
    },
    metrics_drift: (i, base) => ({
      funding: base.funding - Math.min(0.14, i * 0.002),
      fearGreed: Math.max(8, base.fearGreed - Math.floor(i * 0.25)),
      liquidations: base.liquidations + (i > 28 ? i * 18 : i * 2),
      openInterest: base.openInterest * (1 - i * 0.004),
      dominance: base.dominance + Math.sin(i / 12) * 1.2,
    }),
  },
  etf_approval: {
    id: "etf_approval",
    name: { es: "Bitcoin ETF Approval — Enero 2024", en: "Bitcoin ETF Approval — January 2024", sk: "Schválenie Bitcoin ETF — január 2024", de: "Bitcoin ETF Approval — Januar 2024", "zh-CN": "Bitcoin ETF Approval — 2024年1月" },
    free: false,
    startPrice: 44000,
    totalCandles: 80,
    initialCapital: 10000,
    base: { funding: 0.02, openInterest: 1, fearGreed: 52, liquidations: 80, dominance: 52 },
    params: (i) => {
      if (i < 18) return { vol: 0.015, trend: 0.2 };
      if (i < 24) return { vol: 0.045, trend: -0.9 }; // flash crash trap
      if (i < 32) return { vol: 0.04, trend: 0.5 };
      if (i < 40) return { vol: 0.02, trend: 0.15 };
      if (i < 65) return { vol: 0.035, trend: 0.6 }; // rally
      return { vol: 0.025, trend: 0.25 };
    },
    events: {
      20: {
        type: "alert_red",
        text: { es: "Flash crash · Stops barridos", en: "Flash crash · Stops swept", sk: "Flash crash · Stops zmazané", de: "Flash crash · Stops ausgelöst", "zh-CN": "闪崩 · 止损被扫" },
      },
      40: {
        type: "alert_cyan",
        text: { es: "SEC aprueba Bitcoin Spot ETF", en: "SEC approves Bitcoin Spot ETF", sk: "SEC schvaľuje Bitcoin Spot ETF", de: "SEC genehmigt Bitcoin Spot ETF", "zh-CN": "SEC批准Bitcoin现货ETF" },
      },
    },
    metrics_drift: (i, base) => ({
      funding: Math.min(0.14, base.funding + i * 0.0015),
      fearGreed: Math.min(88, base.fearGreed + Math.floor(i * 0.5)),
      liquidations: base.liquidations + (i > 18 && i < 26 ? 220 : i * 4),
      openInterest: base.openInterest * (1 + i * 0.006),
      dominance: base.dominance + i * 0.06,
    }),
  },
  covid_crash: {
    id: "covid_crash",
    name: { es: "COVID Crash — Marzo 2020", en: "COVID Crash — March 2020", sk: "COVID Crash — marec 2020", de: "COVID Crash — März 2020", "zh-CN": "COVID Crash — 2020年3月" },
    free: false,
    startPrice: 9000,
    totalCandles: 80,
    initialCapital: 10000,
    base: { funding: -0.005, openInterest: 1, fearGreed: 38, liquidations: 100, dominance: 64 },
    params: (i) => {
      if (i < 5) return { vol: 0.018, trend: -0.3 };
      if (i < 25) return { vol: 0.055, trend: -0.85 }; // collapse
      if (i < 35) return { vol: 0.04, trend: 0.5 }; // bounce
      if (i < 60) return { vol: 0.03, trend: 0.4 };
      return { vol: 0.022, trend: 0.2 };
    },
    events: {
      10: {
        type: "alert_red",
        text: { es: "GLOBAL PANDEMIC DECLARED", en: "GLOBAL PANDEMIC DECLARED", sk: "GLOBÁLNA PANDÉMIA VYHLÁSENÁ", de: "GLOBALE PANDEMIE ERKLÄRT", "zh-CN": "全球大流行宣布" },
      },
      25: {
        type: "alert_amber",
        text: { es: "Liquidation cascade · Cap. perdida", en: "Liquidation cascade · Capital wiped", sk: "Likvidačná kaskáda · Strata kapitálu", de: "Liquidation cascade · Verlorene Marktkapitalisierung", "zh-CN": "连环爆仓 · 市值损失" },
      },
    },
    metrics_drift: (i, base) => ({
      funding: base.funding - Math.min(0.08, i * 0.0012),
      fearGreed: Math.max(5, base.fearGreed - Math.floor(i * 0.7)),
      liquidations: base.liquidations + (i > 5 && i < 25 ? i * 40 : i * 3),
      openInterest: base.openInterest * (1 - i * 0.008),
      dominance: base.dominance + Math.sin(i / 10) * 1.5,
    }),
  },
  liquidity_sweep: {
    id: "liquidity_sweep",
    name: { es: "Liquidity Sweep — BTC", en: "Liquidity Sweep — BTC", sk: "Liquidity Sweep — BTC", de: "Liquidity Sweep — BTC", "zh-CN": "Liquidity Sweep — BTC" },
    free: false,
    startPrice: 67000,
    totalCandles: 80,
    initialCapital: 10000,
    base: { funding: 0.005, openInterest: 1, fearGreed: 55, liquidations: 70, dominance: 56 },
    params: (i) => {
      if (i < 23) return { vol: 0.012, trend: -0.1 };
      if (i < 27) return { vol: 0.045, trend: -0.8 }; // stop hunt low
      if (i < 35) return { vol: 0.025, trend: 0.6 }; // reclaim
      if (i < 60) return { vol: 0.015, trend: 0.25 };
      return { vol: 0.018, trend: 0.1 };
    },
    events: {
      25: {
        type: "alert_amber",
        text: { es: "Stop hunt detectado · Lows barridos", en: "Stop hunt detected · Lows swept", sk: "Stop hunt detegovaný · Lows zmazané", de: "Stop hunt erkannt · Lows ausgelöst", "zh-CN": "Stop hunt检测到 · 低点被扫" },
      },
    },
    metrics_drift: (i, base) => ({
      funding: base.funding + Math.sin(i / 8) * 0.02,
      fearGreed: base.fearGreed + Math.sin(i / 6) * 8,
      liquidations: base.liquidations + (i > 22 && i < 28 ? 250 : i * 2),
      openInterest: base.openInterest * (1 + Math.sin(i / 10) * 0.05),
      dominance: base.dominance + Math.cos(i / 8) * 0.6,
    }),
  },
};

export const SCENARIO_LIST = Object.values(SCENARIOS);

// Synthetic candle generator following the spec
export function generateCandle(prev, volatility, trend) {
  const change = (Math.random() - 0.48) * volatility * (1 + trend * 0.3);
  const open = prev.close;
  const close = open * (1 + change);
  const wickUp = Math.random() * volatility * 0.5;
  const wickDown = Math.random() * volatility * 0.5;
  const high = Math.max(open, close) * (1 + wickUp);
  const low = Math.min(open, close) * (1 - wickDown);
  const baseVolume = 500;
  const volume = baseVolume * (1 + volatility * 12) * (0.5 + Math.random());
  return { open, high, low, close, volume };
}

export { baseCandle };
